import { useLingui } from '@lingui/react/macro';

import AddressForm from '@/components/AddressForm/AddressForm';
import { InputMoney } from '@/design_system/InputNumber';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { AddressRaw } from '@/models/address';
import { CompanyDetail } from '@/models/company';
import { CURRENCIES, Currency } from '@/utils/number';

type InvoiceData = NonNullable<CompanyDetail['invoiceData']>;

type RegistrationIdType = 'siren' | 'number' | 'company-number' | 'rcs';

export const InvoiceDataForm = ({
  value,
  onChange,
  defaultCountry,
}: {
  value: InvoiceData;
  onChange: (value: InvoiceData) => void;
  defaultCountry: string;
}) => {
  const { t } = useLingui();

  const registrationIdTypeOptions: { id: RegistrationIdType; text: string }[] = [
    {
      id: 'siren',
      text: t({
        id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id-type.siren',
        message: 'SIREN',
      }),
    },
    {
      id: 'number',
      text: t({
        id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id-type.number',
        message: 'Number',
      }),
    },
    {
      id: 'company-number',
      text: t({
        id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id-type.company-number',
        message: 'Company number',
      }),
    },
    {
      id: 'rcs',
      text: t({
        id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id-type.rcs',
        message: 'RCS',
      }),
    },
  ];

  const currencyOptions = CURRENCIES.map((currency) => ({
    id: currency,
    text: currency,
  }));

  const updateInvoiceData = (partial: Partial<InvoiceData>) => {
    onChange({ ...value, ...partial });
  };

  const updateAddress = (address: Partial<AddressRaw> | null) => {
    onChange({
      ...value,
      address: {
        line1: address?.line1 ?? '',
        line2: address?.line2 ?? undefined,
        zipCode: address?.zipCode ?? '',
        city: address?.city ?? '',
        state: address?.state ?? undefined,
        country: address?.country ?? defaultCountry,
      },
    });
  };

  const shareCapitalCurrency = (value.shareCapital?.currency ?? 'EUR') as Currency;
  const shareCapitalAmountCents = value.shareCapital
    ? Math.round(value.shareCapital.amount * 100)
    : undefined;

  return (
    <Stack gap="1rem">
      <InputText
        label={t({
          id: 'settings.organization.invoicing.companies.detail.invoice-data-form.name',
          message: 'Legal name',
        })}
        value={value.name}
        onChange={(name) => updateInvoiceData({ name })}
      />
      <Stack gap="0.5rem">
        <InlineText weight="medium">
          {t({
            id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id',
            message: 'Registration ID',
          })}
        </InlineText>
        <Stack row gap="0.5rem">
          <InputSelect
            variant="select"
            label={t({
              id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id-type',
              message: 'Type',
            })}
            options={registrationIdTypeOptions}
            value={registrationIdTypeOptions.find((option) => option.id === value.id?.type)}
            getOptionValue={(option) => option.text}
            getOptionLabel={(option) => option.text}
            onChange={(option) => {
              const idValue = value.id?.value ?? '';
              const type = option?.id ?? '';

              updateInvoiceData({
                id: type || idValue ? { type, value: idValue } : undefined,
              });
            }}
            isClearable
            isSearchable={false}
            style={{ minWidth: '100px' }}
          />
          <InputText
            label={t({
              id: 'settings.organization.invoicing.companies.detail.invoice-data-form.registration-id-value',
              message: 'Value',
            })}
            value={value.id?.value ?? ''}
            onChange={(idValue) =>
              updateInvoiceData({
                id:
                  idValue || value.id?.type
                    ? { type: value.id?.type ?? '', value: idValue }
                    : undefined,
              })
            }
          />
        </Stack>
      </Stack>
      <AddressForm
        label={t({
          id: 'settings.organization.invoicing.companies.detail.invoice-data-form.address',
          message: 'Address',
        })}
        contact={false}
        value={value.address}
        onChange={updateAddress}
      />
      <InputText
        label={t({
          id: 'settings.organization.invoicing.companies.detail.invoice-data-form.website',
          message: 'Website',
        })}
        value={value.website ?? ''}
        onChange={(website) => updateInvoiceData({ website: website || undefined })}
      />
      <InputText
        label={t({
          id: 'settings.organization.invoicing.companies.detail.invoice-data-form.vat-number',
          message: 'VAT number',
        })}
        value={value.vatNumber ?? ''}
        onChange={(vatNumber) => updateInvoiceData({ vatNumber: vatNumber || undefined })}
      />
      <Stack gap="0.5rem">
        <InlineText weight="medium">
          {t({
            id: 'settings.organization.invoicing.companies.detail.invoice-data-form.share-capital',
            message: 'Share capital',
          })}
        </InlineText>
        <Stack row gap="0.5rem" alignItems="flex-end">
          <InputSelect
            variant="select"
            aria-label={t({
              id: 'settings.organization.invoicing.companies.detail.invoice-data-form.share-capital-currency',
              message: 'Currency',
            })}
            options={currencyOptions}
            value={currencyOptions.find((option) => option.id === shareCapitalCurrency)}
            getOptionValue={(option) => option.text}
            getOptionLabel={(option) => option.text}
            onChange={(option) => {
              if (!option) {
                return;
              }

              updateInvoiceData({
                shareCapital: value.shareCapital
                  ? { ...value.shareCapital, currency: option.id }
                  : { amount: 0, currency: option.id },
              });
            }}
            isSearchable
          />
          <InputMoney
            ariaLabel={t({
              id: 'settings.organization.invoicing.companies.detail.invoice-data-form.share-capital-amount',
              message: 'Share capital amount',
            })}
            currency={shareCapitalCurrency}
            value={shareCapitalAmountCents}
            onChange={(amountCents) => {
              if (amountCents === undefined) {
                updateInvoiceData({ shareCapital: undefined });

                return;
              }

              updateInvoiceData({
                shareCapital: {
                  amount: amountCents / 100,
                  currency: shareCapitalCurrency,
                },
              });
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export const normalizeInvoiceDataForSave = (
  invoiceData: InvoiceData | null
): InvoiceData | null => {
  if (!invoiceData) {
    return null;
  }

  if (!invoiceData.name.trim()) {
    return null;
  }

  const { shareCapital, ...rest } = invoiceData;

  return {
    ...rest,
    name: invoiceData.name.trim(),
    shareCapital: shareCapital && shareCapital.amount > 0 ? shareCapital : undefined,
  };
};
